import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  Clock, LogIn, LogOut, Calendar, DollarSign, TrendingUp,
  Users, Scissors, ChevronLeft, ChevronRight, RefreshCw,
  CheckCircle2, XCircle, AlertTriangle, Timer, KeyRound, X, Check
} from "lucide-react";
import PulseCard from "../components/PulseCard";
import MissedPunchCard from "../components/MissedPunchCard";
import TimeOffCard from "../components/TimeOffCard";

const STATUS_COLORS = {
  booked:      { bg: "#EFF6FF", text: "#1D4ED8", label: "Booked" },
  confirmed:   { bg: "#EFF6FF", text: "#1D4ED8", label: "Booked" },
  late:        { bg: "#FFF7ED", text: "#C2410C", label: "Late" },
  "in-service":{ bg: "#F0FDF4", text: "#15803D", label: "In Service" },
  "no-show":   { bg: "#FEF2F2", text: "#B91C1C", label: "No-Show" },
  completed:   { bg: "#F0FDF4", text: "#16A34A", label: "Completed" },
  cancelled:   { bg: "#FEF2F2", text: "#DC2626", label: "Cancelled" },
  pending:     { bg: "#F8FAFC", text: "#64748B", label: "Pending" },
};

export default function EmployeePortalPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [portalData, setPortalData] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clockLoading, setClockLoading] = useState(false);
  const [clockedIn, setClockedIn] = useState(false);
  const [clockInTime, setClockInTime] = useState(null);
  const [elapsed, setElapsed] = useState("");
  const [dayOffset, setDayOffset] = useState(0);
  const [now, setNow] = useState(new Date());
  const [hasPin, setHasPin] = useState(null); // null=loading, true/false
  const [showPinSetup, setShowPinSetup] = useState(false);
  const [pinDigits, setPinDigits] = useState(["", "", "", ""]);
  const [pinConfirm, setPinConfirm] = useState(["", "", "", ""]);
  const [pinStep, setPinStep] = useState(1); // 1=enter, 2=confirm
  const [pinSaving, setPinSaving] = useState(false);
  const [pinError, setPinError] = useState("");
  const [pinDismissed, setPinDismissed] = useState(false);

  const selectedDate = new Date();
  selectedDate.setDate(selectedDate.getDate() + dayOffset);
  const dateStr = selectedDate.toISOString().slice(0, 10);
  const isToday = dayOffset === 0;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [portalRes, scheduleRes] = await Promise.all([
        api.get("/my/portal"),
        api.get(`/appointments?provider_id=${user.user_id}&start_date=${dateStr}T00:00:00&end_date=${dateStr}T23:59:59`),
      ]);
      setPortalData(portalRes.data);
      const appts = (scheduleRes.data || []).filter(a => a.status !== "cancelled").sort((a, b) => a.start_time.localeCompare(b.start_time));
      setSchedule(appts);
      const clocked = portalRes.data?.is_clocked_in;
      setClockedIn(!!clocked);
      setClockInTime(portalRes.data?.clock_in_time || null);
      // Check if user has a PIN
      try {
        const pinStaff = await api.get("/employee-pins/staff");
        const meHasPin = (pinStaff.data || []).some(s => s.user_id === user.user_id);
        setHasPin(meHasPin);
      } catch { setHasPin(false); }
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  }, [user, dateStr]);

  useEffect(() => { load(); }, [load]);

  // Live clock
  useEffect(() => {
    const tick = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(tick);
  }, []);

  // Elapsed time
  useEffect(() => {
    if (!clockedIn || !clockInTime) { setElapsed(""); return; }
    const tick = setInterval(() => {
      const start = new Date(clockInTime);
      const diff = Math.floor((Date.now() - start.getTime()) / 1000);
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      setElapsed(`${h}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`);
    }, 1000);
    return () => clearInterval(tick);
  }, [clockedIn, clockInTime]);

  const handleClock = async () => {
    setClockLoading(true);
    try {
      if (clockedIn) {
        await api.post("/timeclock/clock-out", { user_id: user.user_id });
        toast.success("Clocked out");
        setClockedIn(false); setClockInTime(null); setElapsed("");
      } else {
        await api.post("/timeclock/clock-in", { user_id: user.user_id });
        toast.success("Clocked in");
        setClockedIn(true); setClockInTime(new Date().toISOString());
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setClockLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const d = portalData || {};
  const earnings = d.earnings || {};
  const stats = d.today_stats || {};
  const completed = schedule.filter(a => a.status === "completed").length;
  const remaining = schedule.filter(a => ["booked", "confirmed", "pending"].includes(a.status)).length;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="employee-portal">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-10">
        <div className="max-w-lg mx-auto flex items-center justify-between px-5 h-14">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl gradient-primary flex items-center justify-center text-white text-sm font-semibold shadow-sm">
              {user?.name?.[0] || "E"}
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900 font-['Manrope']">{user?.name}</p>
              <p className="text-[10px] text-slate-400 capitalize font-['Manrope']">{user?.title || user?.role?.replace("_", " ")}</p>
            </div>
          </div>
          <button onClick={logout} className="text-slate-400 hover:text-rose-500 transition-colors p-2 rounded-lg" data-testid="emp-logout-btn">
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-5 py-6 space-y-5">
        {/* Set Your PIN Banner */}
        {hasPin === false && !pinDismissed && !showPinSetup && (
          <div className="bg-gradient-to-r from-violet-50 to-rose-50 border border-violet-200/50 rounded-2xl p-4 flex items-center gap-3" data-testid="pin-setup-banner">
            <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center flex-shrink-0">
              <KeyRound className="w-5 h-5 text-violet-500" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 font-['Manrope']">Set up quick PIN login</p>
              <p className="text-xs text-slate-400 font-['Manrope']">Skip the password next time — tap your name and enter a 4-digit PIN</p>
            </div>
            <Button size="sm" onClick={() => { setShowPinSetup(true); setPinStep(1); setPinDigits(["","","",""]); setPinConfirm(["","","",""]); setPinError(""); }}
              className="h-8 bg-violet-600 hover:bg-violet-700 text-white rounded-lg px-4 text-xs font-semibold flex-shrink-0" data-testid="setup-pin-btn">
              Set PIN
            </Button>
            <button onClick={() => setPinDismissed(true)} className="text-slate-300 hover:text-slate-500 transition-colors flex-shrink-0 p-1" data-testid="dismiss-pin-banner">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Weekly Pulse */}
        <PulseCard userName={user?.name} />

        {/* Missed Punch Card */}
        <MissedPunchCard />

        {/* Time Off Card */}
        <TimeOffCard />

        {/* Inline PIN Setup */}
        {showPinSetup && (
          <div className="bg-white border border-violet-200 rounded-2xl p-5 space-y-4" data-testid="pin-setup-form">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <KeyRound className="w-4 h-4 text-violet-500" />
                <h3 className="text-sm font-semibold text-slate-800 font-['Outfit']">
                  {pinStep === 1 ? "Choose a 4-digit PIN" : "Confirm your PIN"}
                </h3>
              </div>
              <button onClick={() => setShowPinSetup(false)} className="text-slate-300 hover:text-slate-500 p-1"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex justify-center gap-4">
              {(pinStep === 1 ? pinDigits : pinConfirm).map((d, i) => (
                <div key={i} className={`w-12 h-14 rounded-xl border-2 flex items-center justify-center text-xl font-semibold transition-all ${
                  d ? "border-violet-500 bg-violet-50 text-violet-700" : "border-slate-200 bg-slate-50 text-slate-300"
                }`}>
                  {d ? "•" : ""}
                </div>
              ))}
            </div>
            {pinError && <p className="text-sm text-rose-500 text-center font-semibold" data-testid="pin-setup-error">{pinError}</p>}
            <div className="grid grid-cols-3 gap-2 max-w-[240px] mx-auto">
              {[1,2,3,4,5,6,7,8,9].map(d => (
                <button key={d} onClick={() => {
                  const arr = pinStep === 1 ? [...pinDigits] : [...pinConfirm];
                  const idx = arr.findIndex(x => x === "");
                  if (idx === -1) return;
                  arr[idx] = String(d);
                  if (pinStep === 1) setPinDigits(arr); else setPinConfirm(arr);
                  setPinError("");
                  // Auto-advance on 4th digit
                  if (idx === 3 && pinStep === 1) {
                    setTimeout(() => { setPinStep(2); setPinConfirm(["","","",""]); }, 300);
                  }
                  if (idx === 3 && pinStep === 2) {
                    // Validate match
                    const p = pinDigits.join("");
                    const c = arr.join("");
                    if (p !== c) {
                      setTimeout(() => { setPinError("PINs don't match. Try again."); setPinConfirm(["","","",""]); }, 300);
                    } else {
                      // Save PIN
                      setTimeout(async () => {
                        setPinSaving(true);
                        try {
                          await api.post("/employee-pins/set", { pin: p });
                          toast.success("PIN set! Use it next time you log in.");
                          setShowPinSetup(false);
                          setHasPin(true);
                        } catch { setPinError("Failed to save PIN"); }
                        setPinSaving(false);
                      }, 300);
                    }
                  }
                }}
                  data-testid={`setup-key-${d}`}
                  className="h-12 rounded-xl bg-slate-50 border border-slate-200 text-lg font-semibold text-slate-700 hover:bg-violet-50 hover:border-violet-300 active:scale-95 transition-all font-['Outfit']">
                  {d}
                </button>
              ))}
              <div />
              <button onClick={() => {
                const arr = pinStep === 1 ? [...pinDigits] : [...pinConfirm];
                const idx = arr.findIndex(x => x === "");
                if (idx === -1) return;
                arr[idx] = "0";
                if (pinStep === 1) setPinDigits(arr); else setPinConfirm(arr);
                setPinError("");
                // Auto-advance on 4th digit (same logic as 1-9 keys)
                if (idx === 3 && pinStep === 1) {
                  setTimeout(() => { setPinStep(2); setPinConfirm(["","","",""]); }, 300);
                }
                if (idx === 3 && pinStep === 2) {
                  // Validate match
                  const p = pinDigits.join("");
                  const c = arr.join("");
                  if (p !== c) {
                    setTimeout(() => { setPinError("PINs don't match. Try again."); setPinConfirm(["","","",""]); }, 300);
                  } else {
                    // Save PIN
                    setTimeout(async () => {
                      setPinSaving(true);
                      try {
                        await api.post("/employee-pins/set", { pin: p });
                        toast.success("PIN set! Use it next time you log in.");
                        setShowPinSetup(false);
                        setHasPin(true);
                      } catch { setPinError("Failed to save PIN"); }
                      setPinSaving(false);
                    }, 300);
                  }
                }
              }} data-testid="setup-key-0"
                className="h-12 rounded-xl bg-slate-50 border border-slate-200 text-lg font-semibold text-slate-700 hover:bg-violet-50 hover:border-violet-300 active:scale-95 transition-all font-['Outfit']">
                0
              </button>
              <button onClick={() => {
                const arr = pinStep === 1 ? [...pinDigits] : [...pinConfirm];
                const lastIdx = arr.reduce((last, x, i) => x ? i : last, -1);
                if (lastIdx >= 0) { arr[lastIdx] = ""; if (pinStep === 1) setPinDigits(arr); else setPinConfirm(arr); }
              }} data-testid="setup-delete"
                className="h-12 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400 hover:bg-rose-50 hover:text-rose-500 active:scale-95 transition-all">
                <X className="w-5 h-5" />
              </button>
            </div>
            {pinSaving && <p className="text-xs text-slate-400 text-center">Saving...</p>}
            <div className="flex items-center justify-center gap-2 pt-1">
              <div className={`w-2 h-2 rounded-full transition-colors ${pinStep >= 1 ? "bg-violet-500" : "bg-slate-200"}`} />
              <div className={`w-2 h-2 rounded-full transition-colors ${pinStep >= 2 ? "bg-violet-500" : "bg-slate-200"}`} />
            </div>
          </div>
        )}
        {/* Live Clock + Clock In/Out */}
        <div className="text-center space-y-4" data-testid="clock-section">
          <p className="text-4xl font-semibold text-slate-900 font-['Outfit'] tabular-nums tracking-tight">
            {now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </p>
          <p className="text-sm text-slate-400 font-['Manrope']">
            {now.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })}
          </p>

          <button
            onClick={handleClock}
            disabled={clockLoading}
            data-testid="clock-btn"
            className={`w-32 h-32 rounded-full mx-auto flex flex-col items-center justify-center gap-1 shadow-lg transition-all duration-300 active:scale-95 ${
              clockedIn
                ? "bg-gradient-to-br from-emerald-500 to-teal-600 shadow-emerald-500/30 hover:shadow-emerald-500/50"
                : "bg-gradient-to-br from-rose-500 to-orange-500 shadow-rose-500/30 hover:shadow-rose-500/50"
            }`}
          >
            {clockLoading ? (
              <RefreshCw className="w-7 h-7 text-white animate-spin" />
            ) : clockedIn ? (
              <>
                <LogOut className="w-7 h-7 text-white" strokeWidth={1.5} />
                <span className="text-white text-xs font-semibold">Clock Out</span>
              </>
            ) : (
              <>
                <LogIn className="w-7 h-7 text-white" strokeWidth={1.5} />
                <span className="text-white text-xs font-semibold">Clock In</span>
              </>
            )}
          </button>

          {clockedIn && elapsed && (
            <div className="flex items-center justify-center gap-2" data-testid="elapsed-time">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-sm font-semibold text-emerald-600 tabular-nums font-['Manrope']">{elapsed}</span>
            </div>
          )}
          {!clockedIn && (
            <p className="text-xs text-slate-400 font-['Manrope']">Tap to start your shift</p>
          )}
        </div>

        {/* My Numbers */}
        <div data-testid="numbers-section">
          <h2 className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-3 font-['Manrope']">My Numbers</h2>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.03)]">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-xl bg-rose-50 flex items-center justify-center">
                  <DollarSign className="w-4 h-4 text-rose-500" strokeWidth={1.5} />
                </div>
                <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Today</span>
              </div>
              <p className="text-2xl font-semibold text-slate-900 font-['Outfit']">${stats.revenue || 0}</p>
              <p className="text-[10px] text-slate-400 mt-0.5">revenue</p>
            </div>
            <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.03)]">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-xl bg-violet-50 flex items-center justify-center">
                  <Scissors className="w-4 h-4 text-violet-500" strokeWidth={1.5} />
                </div>
                <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Services</span>
              </div>
              <p className="text-2xl font-semibold text-slate-900 font-['Outfit']">{completed}<span className="text-sm text-slate-400 ml-1">/ {schedule.length}</span></p>
              <p className="text-[10px] text-slate-400 mt-0.5">completed</p>
            </div>
            <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.03)]">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-xl bg-teal-50 flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-teal-500" strokeWidth={1.5} />
                </div>
                <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">This Week</span>
              </div>
              <p className="text-2xl font-semibold text-slate-900 font-['Outfit']">${earnings.week_revenue || 0}</p>
              <p className="text-[10px] text-slate-400 mt-0.5">revenue</p>
            </div>
            <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.03)]">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-xl bg-amber-50 flex items-center justify-center">
                  <Users className="w-4 h-4 text-amber-500" strokeWidth={1.5} />
                </div>
                <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Clients</span>
              </div>
              <p className="text-2xl font-semibold text-slate-900 font-['Outfit']">{earnings.week_services || 0}</p>
              <p className="text-[10px] text-slate-400 mt-0.5">this week</p>
            </div>
          </div>
        </div>

        {/* Schedule */}
        <div data-testid="schedule-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[11px] uppercase tracking-wider text-slate-400 font-bold font-['Manrope']">
              {isToday ? "Today's Schedule" : selectedDate.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}
            </h2>
            <div className="flex items-center gap-1">
              <button onClick={() => setDayOffset(d => d - 1)} className="w-7 h-7 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 hover:bg-slate-50" data-testid="prev-day-btn">
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              {!isToday && (
                <button onClick={() => setDayOffset(0)} className="px-2 py-1 rounded-lg text-[10px] font-semibold text-rose-500 bg-rose-50 hover:bg-rose-100 transition-colors" data-testid="today-btn">
                  Today
                </button>
              )}
              <button onClick={() => setDayOffset(d => d + 1)} className="w-7 h-7 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 hover:bg-slate-50" data-testid="next-day-btn">
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {schedule.length === 0 ? (
            <div className="bg-white border border-slate-100 rounded-2xl p-8 text-center shadow-[0_2px_8px_rgba(0,0,0,0.03)]">
              <Calendar className="w-8 h-8 text-slate-200 mx-auto mb-2" strokeWidth={1.5} />
              <p className="text-sm text-slate-400 font-['Manrope']">{isToday ? "No appointments today" : "No appointments this day"}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {schedule.map((appt, i) => {
                const startTime = new Date(appt.start_time);
                const endTime = new Date(appt.end_time);
                const timeStr = `${startTime.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} - ${endTime.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
                const sc = STATUS_COLORS[appt.status] || STATUS_COLORS.booked;
                const isNow = isToday && now >= startTime && now <= endTime;

                return (
                  <div key={appt.appointment_id} data-testid={`appt-${appt.appointment_id}`}
                    className={`bg-white border rounded-2xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.03)] transition-all ${
                      isNow ? "border-rose-300 ring-2 ring-rose-100" : "border-slate-100"
                    }`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-1.5 rounded-full self-stretch" style={{ backgroundColor: sc.text, minHeight: "40px" }} />
                        <div>
                          <p className="text-sm font-semibold text-slate-900 font-['Manrope']">{appt.client_name}</p>
                          <p className="text-xs text-slate-500 font-['Manrope'] mt-0.5">{appt.service_names?.join(", ")}</p>
                          <div className="flex items-center gap-2 mt-1.5">
                            <span className="text-xs text-slate-400 font-['Manrope'] flex items-center gap-1">
                              <Clock className="w-3 h-3" />{timeStr}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1.5">
                        <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md" style={{ backgroundColor: sc.bg, color: sc.text }}>
                          {sc.label}
                        </span>
                        {isNow && (
                          <span className="text-[9px] font-bold text-rose-500 flex items-center gap-0.5">
                            <div className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" /> NOW
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Summary bar */}
          {schedule.length > 0 && (
            <div className="flex items-center justify-between mt-3 px-1">
              <span className="text-xs text-slate-400 font-['Manrope']">{schedule.length} appointments</span>
              <div className="flex gap-3">
                <span className="text-xs text-emerald-500 font-semibold font-['Manrope']">{completed} done</span>
                <span className="text-xs text-blue-500 font-semibold font-['Manrope']">{remaining} remaining</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
